import {
  booControllerCreateRule,
  booControllerDeleteRule,
  booControllerGetMatrix,
  booControllerListRules,
  booControllerSetOverride,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { recordStagedRuleChange } from '../change-control/staged-changes';
import type {
  RunSheetsMatrixResponse,
  CreateRelevanceRuleInput,
  ProductOperationOverrideInput,
  RelevanceRule,
} from './types';

export const RUN_SHEETS_KEY = 'run_sheets' as const;

// Cross-feature key used for invalidation when rules change.
// Mirrors `OPERATIONS_KEY` in `features/operations/queries.ts` — kept
// inline rather than imported to avoid a circular module ref between
// the two features' queries files.
const OPERATIONS_KEY = 'operations' as const;

// ─── Matrix query ────────────────────────────────────────────────────

/**
 * Query key for the Run Sheet matrix.
 *
 * The partner-wide view and the per-project view must NOT share a cache
 * entry: the project view fetches a project-scoped, un-truncated response
 * (`project_id` param), whereas the partner-wide view fetches the capped
 * partner response. So the project id is appended when present.
 *
 * The partner-wide key stays the bare `[KEY, 'matrix', partnerId]`, which
 * is a prefix of every project-scoped key — so `invalidateQueries` on the
 * bare key (used by the mutations below) refreshes both views, while the
 * optimistic `get/setQueryData` calls target the exact scoped key.
 */
export function runSheetsMatrixQueryKey(
  partnerId: string | undefined,
  projectId?: string,
  filters?: RunSheetsMatrixFilters,
) {
  const base = projectId
    ? ([RUN_SHEETS_KEY, 'matrix', partnerId, projectId] as const)
    : ([RUN_SHEETS_KEY, 'matrix', partnerId] as const);
  const containerId = filters?.containerId ?? null;
  // An option id cannot be resolved without its container (the API 400s on it),
  // so the request drops a dangling one — only a hand-edited or stale URL can
  // produce it, and erroring the whole view over that would be worse than
  // ignoring it. Normalise it away HERE so the key can never claim a filter the
  // request did not apply, which would cache an unfiltered response under a
  // filtered key. Every caller — the view and the optimistic mutations — routes
  // through this function, so they all agree.
  const containerOptionId = containerId === null ? null : (filters?.containerOptionId ?? null);
  // Unfiltered keys stay byte-identical to the pre-ENG-820 shape, so the
  // optimistic get/setQueryData in the mutations below keep hitting the entry
  // the view reads. The filter rides in a trailing object rather than a bare
  // id — appending a raw uuid would land in the project id's slot on the
  // partner-wide view.
  if (containerId === null) return base;
  return [...base, { containerId, containerOptionId }] as const;
}

/**
 * Server-side container filter (ENG-820). The matrix caps products at 200
 * *after* filtering, so an option held only by products past the unfiltered
 * page still resolves — which client-side filtering could never do.
 */
export interface RunSheetsMatrixFilters {
  containerId?: string | null;
  containerOptionId?: string | null;
}

/**
 * Slow safety net for a view that renders release-run progress. Realtime is the
 * PRIMARY update path and it does not refetch at all:
 * `use-change-control-realtime.ts` patches `active_release_run_total` /
 * `_completed` straight into this cache on every `run_changed` event (up to 2 Hz,
 * ~200 bytes, monotonic), and invalidates the matrix once on run start and on a
 * terminal status. So this interval only has to cover a dropped socket — hence
 * 30 s rather than the 2 s this used to be (NEST-BACKEND-Z4).
 *
 * Rebuilding the matrix is expensive: 8 sequential queries in `buildMatrix` plus
 * the workload bands, 480–692 ms in production, and the project-scoped variant is
 * uncapped. Polling it at 2 s turned one slow moment into twelve consecutive 60 s
 * statement timeouts.
 */
const MATRIX_RELEASE_SAFETY_NET_MS = 30_000;

export function runSheetsMatrixQueryOptions(
  partnerId: string | undefined,
  projectId?: string,
  /**
   * `releaseProgress` opts this caller into the safety-net refetch above. Set it
   * ONLY on a view that actually renders `active_release_run_*` — today just the
   * Run Sheets workspace, for the "Releasing… N/M" badge. Every other caller
   * reads static fields (`matrix.operations` for a dropdown, the release
   * playback's five product fields), so a progress refetch buys them nothing.
   */
  opts: { releaseProgress?: boolean } & RunSheetsMatrixFilters = {},
) {
  const { releaseProgress, containerId, containerOptionId } = opts;
  return queryOptions({
    queryKey: runSheetsMatrixQueryKey(partnerId, projectId, opts),
    queryFn: async ({ signal }) => {
      const res = await booControllerGetMatrix(
        {
          partner_id: partnerId ?? '',
          ...(projectId ? { project_id: projectId } : {}),
          // An option id alone is a 400 — the backend can't resolve which
          // container it belongs to — so it only travels with its container.
          ...(containerId
            ? {
                container_id: containerId,
                ...(containerOptionId ? { container_option_id: containerOptionId } : {}),
              }
            : {}),
        },
        { signal },
      );
      // The generated DTO is structurally identical to the locally-defined
      // `RunSheetsMatrixResponse` (which the rest of the feature is
      // typed against); cast at the boundary to keep that single shape.
      return expectData<RunSheetsMatrixResponse>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // Only a view that renders the progress badge refetches at all, and only
    // while a run is actually in flight. Everything else rides staleTime plus
    // realtime's invalidations.
    refetchInterval: (query) => {
      if (!releaseProgress) return false;
      const data = query.state.data;
      if (!data) return false;
      const anyActive = data.products.some((p) => p.active_release_run_id !== null);
      return anyActive ? MATRIX_RELEASE_SAFETY_NET_MS : false;
    },
  });
}

// ─── List rules query (admin only) ───────────────────────────────────

export function relevanceRulesListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [RUN_SHEETS_KEY, 'rules-list', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await booControllerListRules({ partner_id: partnerId ?? '' }, { signal });
      return expectData<RelevanceRule[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create rule mutation (admin only) ───────────────────────────────

export function useCreateRelevanceRule(partnerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRelevanceRuleInput) => {
      const res = await booControllerCreateRule(input);
      return expectData<RelevanceRule>(res);
    },
    onSuccess: (_data, input) => {
      // Rule edits don't move the matrix release counts, so record them in
      // the session-scoped staged-changes registry that drives the
      // AffordanceBar (see features/change-control/staged-changes.ts).
      if (partnerId) recordStagedRuleChange(partnerId, input.operation_id);
      // Both the matrix (cells change) and the rules list (new row) need to refresh.
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix', partnerId],
      });
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'rules-list', partnerId],
      });
      // The operation detail page's Rules section + the Run Sheet usage
      // table's resolved cycle/headcount columns both depend on rules.
      // Invalidate the prefixes so any open operation detail picks up
      // the new rule.
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'rules'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'run-sheets'] });
    },
  });
}

// ─── Delete rule mutation (admin only) ───────────────────────────────

interface DeleteRelevanceRuleArgs {
  ruleId: string;
  /** Operation the rule targets — used to record the staged change. */
  operationId: string;
}

export function useDeleteRelevanceRule(partnerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId }: DeleteRelevanceRuleArgs) => {
      await booControllerDeleteRule(ruleId);
    },
    onSuccess: (_data, { operationId }) => {
      // Rule edits don't move the matrix release counts, so record them in
      // the session-scoped staged-changes registry that drives the
      // AffordanceBar (see features/change-control/staged-changes.ts).
      if (partnerId) recordStagedRuleChange(partnerId, operationId);
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix', partnerId],
      });
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'rules-list', partnerId],
      });
      // Mirror create's cross-feature invalidations (see comment there).
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'rules'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'run-sheets'] });
    },
  });
}

// ─── Set product-operation override mutation ────────────────────────

interface SetOverrideArgs {
  productId: string;
  operationId: string;
  input: ProductOperationOverrideInput;
}

export function useSetProductOperationOverride(
  partnerId: string | undefined,
  projectId?: string,
  // The active container filter is part of the matrix cache key, so the
  // optimistic patch has to target the same entry the view is reading.
  filters?: RunSheetsMatrixFilters,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, operationId, input }: SetOverrideArgs) => {
      await booControllerSetOverride(productId, operationId, input);
    },
    // Optimistic update: flip the targeted cell immediately so the UI
    // feels instant. On error, roll back via the rollback context; on
    // success, invalidate to re-fetch the authoritative state (which
    // also picks up any materialiser side effects).
    onMutate: async ({ productId, operationId, input }) => {
      const queryKey = runSheetsMatrixQueryKey(partnerId, projectId, filters);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<RunSheetsMatrixResponse>(queryKey);
      if (previous) {
        const exists = previous.cells.some(
          (c) => c.product_id === productId && c.operation_id === operationId,
        );

        let updatedCells: RunSheetsMatrixResponse['cells'];
        if (exists) {
          updatedCells = previous.cells.map((c) => {
            if (c.product_id !== productId || c.operation_id !== operationId) return c;
            if (
              input.is_excluded === false &&
              input.cycle_time_override === null &&
              input.headcount_override === null
            ) {
              return {
                ...c,
                applied: true,
                source: 'rule_include' as const,
                cycle_time_override: null,
                headcount_override: null,
                // The override was just touched — mark pending until release.
                is_unreleased: true,
              };
            }
            return {
              ...c,
              applied: input.is_excluded ? false : c.applied,
              source: input.is_excluded ? ('override_exclude' as const) : c.source,
              cycle_time_override: input.cycle_time_override,
              headcount_override: input.headcount_override,
              is_unreleased: true,
            };
          });
        } else {
          // Cell was no_match (not in sparse array) — add it
          updatedCells = [
            ...previous.cells,
            {
              product_id: productId,
              operation_id: operationId,
              applied: !input.is_excluded,
              source: input.is_excluded ? ('override_exclude' as const) : ('rule_include' as const),
              rule_id: null,
              cycle_time_override: input.cycle_time_override,
              headcount_override: input.headcount_override,
              confirmed_workload_category: null,
              suggested_workload_category: 3,
              data_state: 'baseline' as const,
              suggested_data_state: 'baseline' as const,
              is_unreleased: true,
            },
          ];
        }

        queryClient.setQueryData<RunSheetsMatrixResponse>(queryKey, {
          ...previous,
          cells: updatedCells,
        });
      }
      return { previous };
    },
    onError: (_err, _args, context) => {
      const queryKey = runSheetsMatrixQueryKey(partnerId, projectId, filters);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix', partnerId],
      });
    },
  });
}

// ─── Confirm workload category mutation (ENG-609) ────────────────────

interface ConfirmWorkloadCategoryArgs {
  productId: string;
  operationId: string;
  /** 1..5 to confirm, or null to clear back to Baseline. */
  confirmedCategory: number | null;
  /** Current override values, resent so the upsert preserves them. */
  cycleTimeOverride: string | null;
  headcountOverride: number | null;
}

/**
 * Confirm (or clear) a process workload category on a cell. Hits the same
 * per-product override endpoint as {@link useSetProductOperationOverride}
 * but carries `confirmed_workload_category`; the staged value is captured
 * onto work orders on the next change-control Release. Optimistically flips
 * the cell to Estimated (or back to Baseline) and marks it unreleased.
 */
export function useConfirmWorkloadCategory(
  partnerId: string | undefined,
  projectId?: string,
  filters?: RunSheetsMatrixFilters,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      operationId,
      confirmedCategory,
      cycleTimeOverride,
      headcountOverride,
    }: ConfirmWorkloadCategoryArgs) => {
      const input: ProductOperationOverrideInput = {
        is_excluded: false,
        cycle_time_override: cycleTimeOverride,
        headcount_override: headcountOverride,
        confirmed_workload_category: confirmedCategory,
      };
      await booControllerSetOverride(productId, operationId, input);
    },
    onMutate: async ({ productId, operationId, confirmedCategory }) => {
      const queryKey = runSheetsMatrixQueryKey(partnerId, projectId, filters);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<RunSheetsMatrixResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<RunSheetsMatrixResponse>(queryKey, {
          ...previous,
          cells: previous.cells.map((c) =>
            c.product_id === productId && c.operation_id === operationId
              ? {
                  ...c,
                  confirmed_workload_category: confirmedCategory,
                  data_state: confirmedCategory !== null && c.applied ? 'estimated' : 'baseline',
                  is_unreleased: true,
                }
              : c,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _args, context) => {
      const queryKey = runSheetsMatrixQueryKey(partnerId, projectId, filters);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [RUN_SHEETS_KEY, 'matrix', partnerId],
      });
    },
  });
}
